/*
 * The purpose of this mixin is to abstract the creation and subsquent updating
 * of selection coordinates as the user interacts with the page. The initial
 * coordinates found by calling `offset()` on a selection only remain correct as
 * long as no further interaction with the surrounding DOM elements takes place.
 * For the coordinates to remain corret, we must continually update on scrolling
 * of the editor.
 *
 * The type of selection, (e.g. a range, block, or an enitiy) is abstracted away. Rather than
 * editors having to handle the change in those cases, we simply return a rectangle
 * coordinates for the editor to point at.
 */

import ElementBoundingRectPositioning from '@intercom/embercom-composer/components/mixins/element-bounding-rect-positioning';
import $ from 'jquery';
import { reads } from '@ember/object/computed';
import { bind, cancel, scheduleOnce, throttle } from '@ember/runloop';
import Em from 'ember';
export default Em.Mixin.create(ElementBoundingRectPositioning, {
  /* These values represent the editor's current internal scroll state.
   * Comparing current scroll offsets to the previous offset allows us
   * to calculate the tranlated position of the selection without causing
   * an ineffcient DOM repaint calling `offset()`.
   */
  previousSelectionScrollOffset: {
    x: 0,
    y: 0
  },
  selectionCoordinates: null,
  editorHeight: reads('editorDimensions.height'),
  editorWidth: reads('editorDimensions.width'),
  topTemplateOffset: reads('editorDimensions.emailTemplateOffsetTop'),
  leftTemplateOffset: reads('editorDimensions.emailTemplateOffsetLeft'),
  _getCurrentEntityElement() {
    let entity = this.get('currentEditableEntity');
    if (entity) {
      return $(`[data-id="${entity.get('id')}"]`, this.element);
    }
  },
  _getCurrentBlockElement() {
    let currentBlock = this.get('currentEditableBlock');
    if (currentBlock) {
      return $(`[data-id="${currentBlock.get('id')}"]`, this.element).find('img, video, iframe, a.intercom-h2b-button, a.link-only-mode, .block-html, .block-image-text').first();
    }
  },
  setupSelectionScrollListeners: Em.on('didInsertElement', function () {
    $(this.element).on(`scroll.coordinates${this.get('elementId')}`, bind(this, this._updateCoordinatesForInteralSelectionScroll));
  }),
  teardownSelectionScrollListeners: Em.on('willDestroyElement', function () {
    $(this.element).off(`scroll.coordinates${this.get('elementId')}`);
    cancel('internalSelectionScrollUpdater');
  }),
  // As multiple properties can change within a small period of time, e.g. mousedown and on
  // mouseup, there is no point running once per action when the effects will not be percievable
  // by the user. Instead, run once after render
  setSelectionCoordinates: Em.observer({
    dependentKeys: ['currentEditableEntity', 'currentEditableBlock', 'editorDimensions'],
    fn() {
      scheduleOnce('afterRender', this, this._performSelectionReset);
    },
    sync: false
  }),
  getSelectionDimensions() {
    let selectionDimensions = this.get('uiSelection').getDimensions(this.get('$editor'));
    return {
      width: selectionDimensions.width,
      height: selectionDimensions.height
    };
  },
  getSelectionCoordsRelativeToViewport() {
    let selection = this.get('uiSelection').getCoords(this.get('$editor'));
    if (!selection) {
      return null;
    }
    if (!$(this.element)) {
      return {
        top: 0,
        left: 0
      };
    }

    // We must offset the relative position by the scroll on the document.
    let $doc = $(document);
    selection.y += $doc.scrollTop();
    selection.x += $doc.scrollLeft();
    let offset = $(this.element).offset();
    return {
      top: selection.y - offset.top,
      left: selection.x - offset.left
    };
  },
  _performSelectionReset() {
    if (!this.get('isDestroying')) {
      let currentEntityElement = this._getCurrentEntityElement();
      let currentEditableBlock = this.get('currentEditableBlock');
      if (Em.isPresent(currentEntityElement)) {
        this._setCoordinatesFromSelectedEntity();
      } else if (Em.isPresent(currentEditableBlock) && currentEditableBlock.get('isMedia')) {
        this._setCoordinatesFromSelectedBlock();
      } else {
        this._setCoordinatesFromSelectedRange();
      }
      this._showSelectionDebugOutlines();
    }
  },
  _setCoordinatesFromSelectedRange() {
    let selectionCoords = this.getSelectionCoordsRelativeToViewport();
    let selectionDimensions = this.getSelectionDimensions();
    if (Em.isPresent(selectionCoords)) {
      let topTemplateOffset = this.get('topTemplateOffset');
      let leftTemplateOffset = this.get('leftTemplateOffset');
      let selectionExtrudesViewport = selectionCoords.left + selectionDimensions.width > this.get('editorWidth');
      let left = selectionExtrudesViewport ? parseInt($(this.element).css('padding-left'), 10) : selectionCoords.left;
      let top = selectionCoords.top + topTemplateOffset;
      this.set('selectionCoordinates', {
        top,
        left: left + leftTemplateOffset,
        width: selectionDimensions.width,
        height: selectionDimensions.height,
        isWithinViewport: this._isSelectionWithinViewport(top)
      });
    }
  },
  _setCoordinatesFromSelectedEntity() {
    let $entity = this._getCurrentEntityElement();
    this._setCoordinatesFromElement($entity);
  },
  _setCoordinatesFromSelectedBlock() {
    let $block = this._getCurrentBlockElement();
    this._setCoordinatesFromElement($block);
  },
  _setCoordinatesFromElement($element) {
    if ($element.length) {
      let position = this.relativeBoundingRectPosition(this.get('$editor'), $element);
      let boundingRect = $element[0].getBoundingClientRect();
      this.set('selectionCoordinates', {
        top: position.top + this.get('topTemplateOffset'),
        left: position.left + this.get('leftTemplateOffset'),
        width: boundingRect.width,
        height: boundingRect.height,
        isWithinViewport: this._isSelectionWithinViewport(position.top + this.get('topTemplateOffset'))
      });
    }
  },
  _shouldUpdateSelectionPosition() {
    return Em.isPresent(this.get('selectionCoordinates')) && !this.get('isDestroying');
  },
  // Updates occur every 5ms on the trailing edge so that we correctly position
  // the selection based on when the scrolling stops rather than when it starts.
  _updateCoordinatesForInteralSelectionScroll() {
    this.set('internalSelectionScrollUpdater', throttle(this, this._throttledInternalSelectionScrollUpdate, 5, false));
  },
  _throttledInternalSelectionScrollUpdate() {
    if (this._shouldUpdateSelectionPosition()) {
      let previousOffset = this.get('previousSelectionScrollOffset');
      let currentOffset = {
        x: $(this.element).scrollLeft(),
        y: $(this.element).scrollTop()
      };
      this._updateSelectionPositionUsingOffsets(previousOffset, currentOffset);
      this.set('previousSelectionScrollOffset', currentOffset);
    }
  },
  _updateSelectionPositionUsingOffsets(previousOffset, currentOffset) {
    let horizontalDifference = currentOffset.x - previousOffset.x;
    let verticalDifference = currentOffset.y - previousOffset.y;
    let selectionCoordinates = this.get('selectionCoordinates');
    let updatedLeft = selectionCoordinates.left - horizontalDifference;
    let updatedTop = selectionCoordinates.top - verticalDifference;
    let isWithinViewport = this._isSelectionWithinViewport(updatedTop);
    this.set('selectionCoordinates', {
      left: updatedLeft,
      top: updatedTop,
      width: selectionCoordinates.width,
      height: selectionCoordinates.height,
      isWithinViewport
    });
    this._showSelectionDebugOutlines();
  },
  _isSelectionWithinViewport(selectionTop) {
    // If the selected line is only a few pixels above the viewport is is
    // still readable and we should mark it as being within the viewport.
    let overflowAllowanceForViewportTop = 5;

    // Depending on broweser implementation of getBoundingClientRect(), selectionTop can sometimes be a float.
    // This causes the selectionIsAboveBottomOfViewport check to fail by a tiny margin (.078).
    let flooredSelectionTop = Math.floor(selectionTop);
    let selectionIsBelowTopOfViewport = flooredSelectionTop + overflowAllowanceForViewportTop >= this.get('topTemplateOffset');
    let selectionIsAboveBottomOfViewport = flooredSelectionTop <= this.get('editorHeight') + this.get('topTemplateOffset');
    return selectionIsBelowTopOfViewport && selectionIsAboveBottomOfViewport;
  },
  _showSelectionDebugOutlines() {
    let selectionCoordinates = this.get('selectionCoordinates');
    if (this.get('showDebugOutlines') && selectionCoordinates) {
      $('#dynamic-selection-overlay').css({
        top: selectionCoordinates.top,
        left: selectionCoordinates.left,
        width: selectionCoordinates.width,
        height: selectionCoordinates.height,
        'outline-color': selectionCoordinates.isWithinViewport ? 'darkgreen' : 'crimson'
      });
      $('#editor-top-debug-outline').css({
        top: this.get('topTemplateOffset')
      });
      $('#editor-bottom-debug-outline').css({
        top: this.get('editorHeight') + this.get('topTemplateOffset')
      });
      if (this.get('viewportSelector')) {
        let viewportRect = $(this.get('viewportSelector'))[0].getBoundingClientRect();
        $('#viewport-container-overlay').css({
          top: viewportRect.top,
          left: viewportRect.left,
          width: viewportRect.width,
          height: viewportRect.height
        });
      }
    }
  }
});